import {
  destinationCountriesByZone,
  getRegionName,
  messages,
  type RegionCode,
  statesLabel,
  truthy
} from "@quortex/front-common"
import { DeviceLabels } from "@/components/DeviceEditionForm"
import { NameLengthLimit } from "@/const"
import type { SegmentResponse } from "@/types/api"

export const formatListWithLimit = (itemList: string[] | undefined, limit: number = 5): string => {
  if (!itemList || itemList.length < 1) return ""

  const sortedList = [...itemList].sort()

  const firstItemList = sortedList.slice(0, limit).join(", ")
  const remainingCount = itemList.length - limit

  return remainingCount > 0 ? `${firstItemList} and ${remainingCount} others.` : `${firstItemList}`
}

const list2TruncatedText = (itemList: string[] | undefined, limit: number = 100): string => {
  const joinedText = itemList?.join(", ") || ""

  if (joinedText.length <= limit) return joinedText

  // Truncate and remove trailing incomplete word
  let truncatedText = joinedText.slice(0, limit - 3)
  const lastSpaceIndex = truncatedText.lastIndexOf(" ")

  if (lastSpaceIndex > 0) {
    truncatedText = truncatedText.slice(0, lastSpaceIndex)
  }

  // Remove trailing comma if present
  return truncatedText.endsWith(",") ? `${truncatedText.slice(0, -1)}...` : `${truncatedText}...`
}

export const getFormatedRegionNameMixed = (regions: RegionCode[] = [], limit?: number) => {
  const states = regions.filter(region => region.startsWith("US-"))
  const countries = regions.filter(region => !region.startsWith("US-"))

  return getFormatedRegionName(countries, states, limit)
}

export const getFormatedRegionName = (
  streamingCountries: RegionCode[] = [],
  states: RegionCode[] = [],
  limit?: number
) => {
  // Early returns for simple cases
  if (streamingCountries.length === 1 && states.length === 0) {
    return streamingCountries[0] ? getRegionName(streamingCountries[0]) : ""
  } else if (streamingCountries.length === 0 && states.length === 1) {
    return states[0] ? getRegionName(states[0]) : ""
  }

  // Optimize zone checking with Set for O(1) lookups
  const streamingCountriesSet = new Set(streamingCountries)
  const checkedZones = Object.entries(destinationCountriesByZone)
    .filter(([, countries]) => countries.every(country => streamingCountriesSet.has(country)))
    .map(([zone]) => zone) as (keyof typeof destinationCountriesByZone)[]

  // Build countriesSet once for efficient lookup
  const countriesFromCheckedZones = new Set(
    checkedZones.flatMap(zone => destinationCountriesByZone[zone])
  )

  const orphanCountries = streamingCountries.filter(
    country => !countriesFromCheckedZones.has(country as api.switchns.CountryValue["code"])
  )

  // Early return for worldwide
  if (checkedZones.length === Object.keys(destinationCountriesByZone).length) {
    return "Worldwide"
  }

  // Optimize states processing - avoid unnecessary filter
  if (states.length > 0) {
    const stateLabels = states.map(state => statesLabel[state]).filter(truthy)
    return formatListWithLimit(stateLabels)
  }

  // Compute zone and region labels once
  const zoneLabels = checkedZones.map(zone => messages.en.destination.zone[zone])
  const orphanRegionNames = orphanCountries.map(region => getRegionName(region))
  const allLabels = [...zoneLabels, ...orphanRegionNames]

  const labelWithAndOther = formatListWithLimit(allLabels)

  return limit && labelWithAndOther.length > limit
    ? list2TruncatedText(allLabels)
    : labelWithAndOther
}

export const getNetworkNameFromList = (
  asnList: NonNullable<SegmentResponse["network"]>["asns"] | undefined
): string => {
  if (!asnList) return ""

  // Map once and reuse
  const organizations = asnList.map(asn => asn.organization)
  const nameWithLimit = formatListWithLimit(organizations)

  return nameWithLimit.length > NameLengthLimit
    ? list2TruncatedText(organizations, NameLengthLimit)
    : nameWithLimit
}

export const getDeviceNameFromList = (
  deviceList: SegmentResponse["devices"] | undefined
): string => {
  if (!deviceList) return ""

  return deviceList
    .filter(truthy)
    .sort(sortDevices)
    .map(device => DeviceLabels[device])
    .join(", ")
}

// Memoize priority map for O(1) lookups
const devicePriorityMap = new Map([
  ["android", 0],
  ["ios", 1]
])

const sortDevices = (a: string, b: string) => {
  const aIndex = devicePriorityMap.get(a) ?? -1
  const bIndex = devicePriorityMap.get(b) ?? -1

  // If both are priority devices, maintain their priority order
  if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
  // If only 'a' is priority, it comes first
  if (aIndex !== -1) return -1
  // If only 'b' is priority, it comes first
  if (bIndex !== -1) return 1
  // Neither is priority, sort alphabetically
  return a.localeCompare(b)
}

export const formatBandwidth = (bandwidth: number) => {
  return bandwidth ? Math.ceil(bandwidth * 1000) / 1000 : 0
}

export const formatPercentage = (value: number) => {
  const zerosNeeded = 3 - value.toString().length
  const zeros = "0".repeat(zerosNeeded)

  return (
    <>
      {zerosNeeded > 0 && <span className="invisible">{zeros}</span>}
      {value}
    </>
  )
}
